import React from "react";
import PropTypes from "prop-types";
import { Table, Spinner, Pagination, Form } from "react-bootstrap";

const ReusableTable = ({
  columns,
  data,
  rowKey,
  className,
  loading,
  currentPage,
  totalPages,
  onPageChange,
  recordsPerPage,
  onRecordsPerPageChange,
  isServerSidePagination = false,
}) => {
  // Handle client-side pagination
  // const paginatedData = isServerSidePagination ? data : data?.slice((currentPage - 1) * recordsPerPage, currentPage * recordsPerPage);
  const paginatedData = data; // No need to slice again if pagination is handled outside

  
  return (
    <div className={`table-responsive ${className}`}>
      <Table className="table table-agent mb-0">
        <thead>
          <tr>
            {columns?.map((col, index) => (
              <th key={index} style={{ width: col?.width || "auto" }}>
                {col?.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {loading ? (
            // Show spinner in the table body when loading is true
            <tr>
              <td colSpan={columns?.length} style={{ textAlign: "center", verticalAlign: "middle" }}>
                <Spinner animation="border" variant="primary" />
              </td>
            </tr>
          ) : paginatedData?.length > 0 ? (
            // Render table rows when data is available
            paginatedData?.map((item, rowIndex) => (
              <tr key={item[rowKey] || rowIndex}>
                {columns?.map((col, colIndex) => (
                  <td key={colIndex} style={{ width: col?.width || "auto" }}>
                    {col?.render ? col?.render(item[col?.accessor], item) : item[col?.accessor]}
                  </td>
                ))}
              </tr>
            ))
          ) : (
            // Show "No data available" message when there is no data
            <tr>
              <td colSpan={columns?.length} style={{ textAlign: "center" }}>
                No data available
              </td>
            </tr>
          )}
        </tbody>
      </Table>

      {/* Pagination Controls */}
      <div className="d-flex justify-content-between align-items-center mt-3 px-2">
        {/* Records per page dropdown */}
        <Form.Select
          style={{ width: "auto" }}
          value={recordsPerPage}
          className="mb-3"
          onChange={(e) => onRecordsPerPageChange(Number(e?.target?.value))}
        >
          <option value={10}>10 per page</option>
          <option value={25}>25 per page</option>
          <option value={50}>50 per page</option>
          <option value={100}>100 per page</option>
        </Form.Select>

        {/* Pagination */}
        <Pagination>
          <Pagination.First onClick={() => onPageChange(1)} disabled={currentPage === 1} />
          <Pagination.Prev onClick={() => onPageChange(currentPage - 1)} disabled={currentPage === 1} />

          {Array.from({ length: Math.min(6, totalPages) }, (_, i) => i + 1).map((page) => (
            <Pagination.Item key={page} active={page === currentPage} onClick={() => onPageChange(page)}>
              {page}
            </Pagination.Item>
          ))}

          {totalPages > 6 && currentPage < totalPages - 3 && <Pagination.Ellipsis disabled />}

          {totalPages > 6 && currentPage >= totalPages - 3 && (
            Array.from({ length: 3 }, (_, i) => totalPages - 3 + i).map((page) => (
              <Pagination.Item key={page} active={page === currentPage} onClick={() => onPageChange(page)}>
                {page}
              </Pagination.Item>
            ))
          )}

          {totalPages > 6 && (
            <Pagination.Item active={currentPage === totalPages} onClick={() => onPageChange(totalPages)}>
              {totalPages}
            </Pagination.Item>
          )}

          <Pagination.Next onClick={() => onPageChange(currentPage + 1)} disabled={currentPage === totalPages} />
          <Pagination.Last onClick={() => onPageChange(totalPages)} disabled={currentPage === totalPages} />
        </Pagination>
      </div>
    </div>
  );
};

ReusableTable.propTypes = {
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      header: PropTypes.string.isRequired, // Column header
      accessor: PropTypes.string, // Key for data
      render: PropTypes.func, // Optional custom rendering function
      width: PropTypes.string, // Optional width for the column
    })
  ).isRequired,
  data: PropTypes.array.isRequired, // Data to display
  rowKey: PropTypes.string, // Key to uniquely identify rows
  className: PropTypes.string, // Additional CSS classes for styling
  loading: PropTypes.bool, // Loading state
  currentPage: PropTypes.number.isRequired, // Current page number
  totalPages: PropTypes.number.isRequired, // Total number of pages
  onPageChange: PropTypes.func.isRequired, // Function to handle page change
  recordsPerPage: PropTypes.number.isRequired, // Number of records per page
  onRecordsPerPageChange: PropTypes.func.isRequired, // Function to handle records per page change
  isServerSidePagination: PropTypes.bool, // Whether pagination is server-side
};

ReusableTable.defaultProps = {
  rowKey: "id", // Default row key
  className: "", // Default to no additional classes
  loading: false, // Default to not loading
  isServerSidePagination: false, // Default to client-side pagination
};

export default ReusableTable;
